import { useCallback } from 'react';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import Button from 'fe-design-base/molecules/Button';
import Pill from 'fe-design-base/molecules/Pill';

import {
  acceptTradeCoverRequest,
  rejectTradeCoverRequest,
} from 'features/notifications/api';
import { useRequestAction } from 'features/notifications/hooks/useRequestAction';
import { STATUS_UI } from 'features/notifications/statuses';
import { RequestType, TradeCoverClaim } from 'features/notifications/types';

import { toI18n } from 'util/i18n';

import { BaseRequest } from '../../BaseRequest';
import { ShiftDetails } from '../../ShiftDetails';

type TradeStatus = keyof (typeof STATUS_UI)['trade'];

type TradeProps = {
  request: TradeCoverClaim;
  setSelectedRequest?: (request: TradeCoverClaim, type: RequestType) => void;
};

const Trade = ({ request, setSelectedRequest }: TradeProps) => {
  const { isLoading: isAccepting, handleAction: handleAccept } =
    useRequestAction({
      apiFunction: acceptTradeCoverRequest,
      itemId: request.responder_item_id as string,
      successMessage: 'requests_feed.trade.details.accepted_success',
      initiatedFrom: 'trade_shift_card',
      buttonText: toI18n('requests_feed.actions.accept'),
    });
  const { isLoading: isDeclining, handleAction: handleDecline } =
    useRequestAction({
      apiFunction: rejectTradeCoverRequest,
      itemId: request.responder_item_id as string,
      successMessage: 'requests_feed.trade.details.declined_success',
      initiatedFrom: 'trade_shift_card',
      buttonText: toI18n('requests_feed.actions.no_thanks'),
    });
  const {
    last_status_changed_at_secs: updated_at,
    type,
    requester,
    responder,
    status,
    pending_approval_count,
    shift,
    location,
    viewer_role,
    offered_shift: offeredShift,
    reason,
  } = request;

  const { first_name, last_name, avatar } = requester || {};
  const name = `${first_name} ${last_name}`;
  const responderName = `${responder?.first_name} ${responder?.last_name}`;
  const iAmTheBuyer = viewer_role === 'responder';
  const iAmTheSellerOrOAM =
    viewer_role === 'oam' || viewer_role === 'requester';

  const statusValue = status as TradeStatus;
  const pillUI = STATUS_UI.trade[statusValue];
  const handleClick = useCallback(() => {
    setSelectedRequest?.(request, type);
  }, [setSelectedRequest, request, type]);

  return (
    <BaseRequest name={name} updatedAt={updated_at} type={type} avatar={avatar}>
      <Pill variant={pillUI?.variant}>{pillUI?.label}</Pill>
      {iAmTheSellerOrOAM && (
        <>
          <Text
            variant="captionBold"
            uppercase
            i18n="requests_feed.trade.pending_claims"
            i18nProps={{ count: pending_approval_count || 0 }}
          />
          <ShiftDetails shift={shift} location={location} />
        </>
      )}

      {iAmTheBuyer && offeredShift && (
        <Box column gap={8}>
          <div>
            <Text
              variant="bodyBoldTight"
              i18n="requests_feed.trade.details.requester_name"
              i18nProps={{ requester_name: name }}
            />
            <ShiftDetails shift={offeredShift} location={location} />
          </div>
          <div>
            <Text
              variant="bodyBoldTight"
              i18n="requests_feed.trade.details.responder_name"
              i18nProps={{ responder_name: responderName }}
            />
            <ShiftDetails shift={shift} location={location} />
          </div>
        </Box>
      )}

      {reason && (
        <Box column gap={2}>
          <Text
            variant="bodySmBold"
            color="mono700"
            i18n="requests_feed.trade.note"
          />
          <Text variant="bodySm" color="mono700">
            {reason}
          </Text>
        </Box>
      )}
      {iAmTheSellerOrOAM && (
        <Button fullWidth onClick={handleClick}>
          {toI18n('requests_feed.trade.review')}
        </Button>
      )}
      {iAmTheBuyer && statusValue === 'needs_reply' && (
        <Box row gap={8}>
          <Button
            variant="secondary"
            onClick={handleDecline}
            fullWidth
            disabled={isDeclining || isAccepting}
            isLoading={isDeclining}
          >
            {toI18n('requests_feed.actions.no_thanks')}
          </Button>
          <Button
            fullWidth
            onClick={handleAccept}
            disabled={isAccepting || isDeclining}
            isLoading={isAccepting}
          >
            {toI18n('requests_feed.actions.accept')}
          </Button>
        </Box>
      )}
      {iAmTheBuyer && statusValue === 'pending_approval' && (
        <Button
          variant="secondaryDestructive"
          fullWidth
          disabled={isDeclining}
          isLoading={isDeclining}
          onClick={handleDecline}
        >
          {toI18n('requests_feed.actions.undo_accept')}
        </Button>
      )}
    </BaseRequest>
  );
};

export default Trade;
